import QiscusSDKCore from 'qiscus-sdk-core';
import config from 'config';
import mitt from 'mitt';
import { memStore } from './storage';
import { apiPost, apiGet } from 'services/api';
import { waitFor } from 'util/misc';

export const emitter = mitt();
const qiscus = new QiscusSDKCore();
const log = config.IS_DEV ? console.log : () => {};
const WAIT_FOR_CHAT_LOGIN = 30000;

export async function initializeChat() {
  const chatConfig = memStore.get('chatConfig');

  if (!chatConfig) return;

  await qiscus.init({
    AppId: config.QISCUS_APP_ID,
    syncInterval: 60000,
    options: {
      loginSuccessCallback: function (authData) {
        log('loginSuccessCallback', authData);
        emitter.emit('login-success', authData);
        emitter.emit('unread-count-change');
      },
      newMessagesCallback: function (messages) {
        log('newMessagesCallback', messages);
        messages.forEach(function (message, index) {
          emitter.emit('new-message', {
            ...message,
            isLast: index === messages.length - 1,
          });
        });
        emitter.emit('unread-count-change');
      },
      presenceCallback: function (data) {
        log('presenceCallback', data);
        var isOnline = data.split(':')[0] === '1';
        var lastOnline = new Date(Number(data.split(':')[1]));
        emitter.emit('online-presence', {
          isOnline: isOnline,
          lastOnline: lastOnline,
        });
      },
      commentReadCallback: function (data) {
        log('commentReadCallback', data);
        emitter.emit('comment-read', data);
      },
      commentDeliveredCallback: function (data) {
        log('commentDeliveredCallback', data);
        emitter.emit('comment-delivered', data);
      },
      typingCallback: function (data) {
        log('typingCallback', data);
        emitter.emit('typing', data);
      },
      commentDeletedCallback: function (data) {
        log('commentDeletedCallback', data);
        emitter.emit('comment-deleted', data);
      },
    },
  });
  await qiscus.setUser(chatConfig.chatId, chatConfig.chatKey);
}

let lastTypedAt = null;

export async function startedTyping(clearInMillis = 3000) {
  log(lastTypedAt ? 'still typing' : 'startedTyping');
  qiscus.publishTyping(1);
  if (lastTypedAt) {
    clearTimeout(lastTypedAt);
  }
  lastTypedAt = setTimeout(() => {
    stoppedTyping();
    lastTypedAt = null;
  }, clearInMillis);
}

export async function stoppedTyping() {
  log('stoppedTyping');
  qiscus.publishTyping(0);
}

export async function chatStatus() {
  const chatConfig = memStore.get('chatConfig');
  if (!chatConfig) return;

  const chatRooms = await fetchChatRoomList();
  const [unreadRooms, unreadMessages] = chatRooms.reduce(
    ([unreadRooms, unreadMessages], { count_notif }) => [
      unreadRooms + (count_notif ? 1 : 0),
      unreadMessages + count_notif,
    ],
    [0, 0]
  );
  return { unreadRooms, unreadMessages };
}

export async function fetchChatRoomList() {
  if (!memStore.get('chatConfig')) return;

  await waitFor(() => qiscus.isLogin, { timeout: WAIT_FOR_CHAT_LOGIN });
  return await qiscus.loadRoomList({
    page: 1,
    limit: 100,
    show_participants: false,
    show_empty: false,
  });
}

export async function fetchChatRoom(roomId) {
  await waitFor(() => qiscus.isLogin, { timeout: WAIT_FOR_CHAT_LOGIN });
  return qiscus.getRoomById(roomId);
}

export async function fetchMoreFromChatRoom(roomId, last_comment_id, limit) {
  return qiscus.loadComments(roomId, {
    last_comment_id,
    limit,
    after: false,
  });
}

export async function subscribe(eventName, handler) {
  eventName && handler && emitter.on(eventName, handler);
}

export async function unsubscribe(eventName, handler) {
  eventName && handler && emitter.on(eventName, handler);
}

export function isChatBotRoomId(roomId) {
  const chatConfig = memStore.get('chatConfig');
  return chatConfig && roomId === `${chatConfig.chatBotRoomId}`;
}

export async function fetchParticipants(roomId) {
  const page = 1;
  const limit = 20;
  await waitFor(() => qiscus.isLogin);
  return await qiscus.getParticipants(roomId, page, limit);
}

export async function sendMessage(roomId, text, type, payload) {
  return await qiscus.sendComment(roomId, text, undefined, type, payload);
}

export async function markMessageRead(roomId, commentId) {
  qiscus.readComment(roomId, commentId);
  setTimeout(() => emitter.emit('unread-count-change'), 1000);
}

export async function createGroupChat(name, participants) {
  const options = {
    avatarURL: config.defaultChatroomAvatar,
  };

  const chatGroup = await qiscus.createGroupRoom(name, participants, options);
  return chatGroup && chatGroup.id;
}

export async function fetchChatRoomFor(userId) {
  const { qiscusRoomId } = await apiPost(`/user/${userId}/chat`, {});
  return qiscusRoomId;
}

export async function loadMoreComments(roomId, lastCommentId, userId) {
  var options = {
    last_comment_id: lastCommentId,
    after: false,
    limit: 20,
  };

  return qiscus.loadComments(roomId, options);
}

export async function fetchUserList() {
  const { users } = await apiGet(`/user`);
  return users;
}

export function getUserPageURL(userId) {
  return `/details/v2/entity8/${userId}`; // TODO: remove hardcoding
}

export function disconnectChat() {
  qiscus.disconnect();
}
